import { useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';

import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory/conditional-hooks-factory';

import type { ForgeUIExtensionQueryOptions } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';
import type { UsePreFilteredExtensionsQueryOptions } from '@atlassian/forge-ui/web-client/useExtensionList';
import {
	usePrefilteredExtensionList as usePrefilteredExtensionListApollo,
	usePrefilteredExtensionListRelay,
} from '@atlassian/forge-ui/web-client/usePrefilteredExtensionList';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import {
	FORGE_MODULE_BYLINE,
	FORGE_MODULE_PAGE_BANNER,
	FORGE_MODULE_SPACE_PAGE,
} from '../ForgeModuleType.ts';

import { getUsePrefilteredExtensionListOptions } from './getUsePrefilteredExtensionListOptions.ts';

export type UseExtensionListProps = {
	moduleType: string;
	queryOptions?: UsePreFilteredExtensionsQueryOptions | ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
	skipQuery?: boolean;
	spaceContextOnly?: boolean;
	skipSpaceDetail?: boolean;
	attachmentId?: string;
	contentId?: string;
	spaceKey?: string;
};

type ExtensionListContext = {
	spaceKey?: string;
	contentId?: string;
	includeHidden: boolean;
	spaceContextOnly: boolean;
};

/**
 * Discovery is filtered by space/page, visibility and context mode, so SSR data
 * (including an empty list) is reusable only for an equivalent request. Space-only
 * requests intentionally ignore contentId. Callers check module eligibility,
 * errors and snapshot lifetime separately.
 */
const matchesExtensionListContext = (
	preloadedContext: ExtensionListContext | undefined,
	currentContext: ExtensionListContext,
): boolean =>
	preloadedContext !== undefined &&
	preloadedContext.spaceKey === currentContext.spaceKey &&
	preloadedContext.includeHidden === currentContext.includeHidden &&
	preloadedContext.spaceContextOnly === currentContext.spaceContextOnly &&
	(currentContext.spaceContextOnly || preloadedContext.contentId === currentContext.contentId);

export const useExtensionList = ({
	moduleType,
	queryOptions,
	returnBlockedExtensions = false,
	skipQuery = false,
	spaceContextOnly,
	skipSpaceDetail = false,
	attachmentId,
	contentId: providedContentId,
	spaceKey: providedSpaceKey,
}: UseExtensionListProps): {
	extensions: Extension[];
	loading: boolean;
	error: Error | undefined;
	isSSRResult?: boolean;
} => {
	const { locale } = useIntl();
	const isTransition = useIsTransition();
	const { cloudId } = useSessionData();
	const [pageSpaceKey] = usePageSpaceKey();
	const spaceKey = providedSpaceKey || pageSpaceKey;
	const [pageContentId] = usePageContentId();
	const contentId = providedContentId || pageContentId;
	const resolvedAttachmentId =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		attachmentId && fg('forge_attachment_display_conditions') ? attachmentId : undefined;
	const { error: spaceError } = useSpaceDetail(spaceKey, { skip: skipSpaceDetail });
	const [ssrExtensionResult] = useState(() => window.__SSR_FORGE_EXTENSIONS__?.[moduleType]);
	const currentContext: ExtensionListContext = {
		spaceKey,
		contentId,
		includeHidden: returnBlockedExtensions,
		spaceContextOnly: Boolean(spaceContextOnly),
	};

	// The boot payload belongs to the initial route. Never reuse it after navigation,
	// including when the user returns to the page that originally produced it.
	// Evaluate eligibility before inspecting the payload so control loads are exposed too.
	const pageBannerSSRResult =
		!skipQuery &&
		!resolvedAttachmentId &&
		moduleType === FORGE_MODULE_PAGE_BANNER &&
		!isTransition &&
		expValEquals('confluence_ssr_forge_page_banner', 'isEnabled', true)
			? ssrExtensionResult
			: undefined;
	const preloadedPageBanners =
		pageBannerSSRResult !== undefined &&
		!pageBannerSSRResult.error &&
		matchesExtensionListContext(pageBannerSSRResult.context, currentContext)
			? pageBannerSSRResult
			: undefined;

	const options = useMemo(
		() =>
			getUsePrefilteredExtensionListOptions({
				cloudId,
				locale,
				moduleType,
				queryOptions: {
					...queryOptions,
					skip: skipQuery || preloadedPageBanners !== undefined,
				},
				includeHidden: returnBlockedExtensions,
				spaceKey,
				contentId,
				...(resolvedAttachmentId ? { attachmentId: resolvedAttachmentId } : {}),
				spaceContextOnly,
			}),
		[
			cloudId,
			locale,
			moduleType,
			queryOptions,
			returnBlockedExtensions,
			skipQuery,
			preloadedPageBanners,
			spaceKey,
			contentId,
			resolvedAttachmentId,
			spaceContextOnly,
		],
	);

	const {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	} = usePrefilteredExtensionList(options);

	useEffect(() => {
		// suppress 403 errors as we can't do much with them
		if (extsError && isUnauthorizedError(extsError)) {
			markErrorAsHandled(extsError);
		}

		// suppress 429 errors for offending users
		if (isTooManyRequestsError(extsError)) {
			markErrorAsHandled(extsError);
		}

		if (spaceError) {
			markErrorAsHandled(spaceError);
		}
	}, [extsError, spaceError]);

	if (preloadedPageBanners) {
		return {
			extensions: preloadedPageBanners.extensions,
			loading: false,
			error: undefined,
			isSSRResult: true,
		};
	}

	if (
		!skipQuery &&
		extsLoading &&
		ssrExtensionResult !== undefined &&
		matchesExtensionListContext(ssrExtensionResult.context, currentContext) &&
		(moduleType === FORGE_MODULE_SPACE_PAGE ||
			(moduleType === FORGE_MODULE_BYLINE &&
				expValEquals('confluence_ssr_byline', 'isEnabled', true)))
	) {
		return {
			extensions: ssrExtensionResult.extensions,
			loading: false,
			error: ssrExtensionResult.error,
		};
	}

	return {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	};
};

const usePrefilteredExtensionList = conditionalHooksFactory(
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	() => fg('forge-ui-migrate-extension-list-query-to-relay'),
	usePrefilteredExtensionListRelay,
	usePrefilteredExtensionListApollo,
);
